<template>
  <div class="avatar-batch-page">
    <div class="batch-header">
      <div>
        <h2>数字人视频生成</h2>
        <p class="batch-desc">输入一段文本，提交 Azure 批量数字人任务，等待生成完成后直接下载视频。</p>
      </div>
      <div class="batch-status-chip" :class="{ ok: runtimeAvailable }">
        {{ runtimeAvailable ? '服务已配置' : '服务未配置' }}
      </div>
    </div>

    <div class="batch-layout">
      <div class="batch-panel form-panel" v-loading="submitting || uploadingBackground" element-loading-text="正在处理，请稍候">
        <div class="panel-title">
          <h3>参数配置</h3>         
        </div>
        <div class="config-shell">
          <div class="config-nav">
            <button type="button" class="config-nav-item" :class="{ active: activeSection === 'avatar' }" @click="activeSection = 'avatar'">
              数字人
            </button>
            <button type="button" class="config-nav-item" :class="{ active: activeSection === 'voice' }" @click="activeSection = 'voice'">
              配音
            </button>
            <button type="button" class="config-nav-item" :class="{ active: activeSection === 'background' }" @click="activeSection = 'background'">
              背景
            </button>
          </div>

          <div class="config-content">
            <div v-show="activeSection === 'avatar'" class="config-pane">
              <div class="field field-first">
                <label>数字人角色</label>
                <div class="avatar-gallery-container">
                  <div class="avatar-gallery">
                    <div v-for="group in avatarCharacterGroups" :key="group.label" class="avatar-group">
                      <div class="avatar-group-title">{{ group.label }}</div>
                      <div class="avatar-card-grid">
                        <button
                          v-for="item in group.items"
                          :key="item.value"
                          type="button"
                          class="avatar-card"
                          :class="{ active: form.character === item.value }"
                          @click="selectAvatar(item)"
                        >
                          <div class="avatar-card-image-box" :class="{ photo: item.category === 'photo' }">
                            <img :src="getAvatarCardImage(item)" :alt="item.label" class="avatar-card-image">
                          </div>
                          <div class="avatar-card-name">{{ item.chineseLabel }}/{{ item.value }}</div>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

            </div>

            <div v-show="activeSection === 'voice'" class="config-pane">
              <div class="field field-first">
                <label>发音人</label>
                <el-select v-model="form.voice" filterable>
                  <el-option v-for="item in voiceOptions" :key="item.value" :label="item.label" :value="item.value" />
                </el-select>
              </div>

              <div class="field">
                <label>输出格式</label>
                <el-select v-model="form.videoFormat">
                  <el-option label="MP4（推荐）" value="mp4" />
                  <el-option label="WebM" value="webm" />
                </el-select>
              </div>
            </div>

            <div v-show="activeSection === 'background'" class="config-pane">
              <div class="field field-first">
                <label>背景模式</label>
                <el-radio-group v-model="form.backgroundMode" size="small">
                  <el-radio-button label="color">纯色背景</el-radio-button>
                  <el-radio-button label="url">背景图 URL</el-radio-button>
                  <el-radio-button label="upload">上传图片 / 视频</el-radio-button>
                </el-radio-group>
              </div>

              <div v-if="form.backgroundMode === 'color'" class="field">
                <label>背景颜色</label>
                <div class="color-row">
                  <el-color-picker v-model="form.backgroundColor" />
                  <el-input v-model="form.backgroundColor" />
                </div>
              </div>

              <div v-if="form.backgroundMode === 'url'" class="field">
                <label>背景图 URL</label>
                <el-input v-model.trim="form.backgroundImage" placeholder="请输入可被 Azure 直接访问的公网图片 URL" />
                <p class="field-tip">URL 模式只支持公网图片地址，不支持本地文件。</p>
              </div>

              <div v-if="form.backgroundMode === 'upload'" class="field">
                <label>上传背景</label>
                <el-upload
                  class="background-uploader"
                  action=""
                  :show-file-list="false"
                  :auto-upload="true"
                  :http-request="uploadBackgroundFile"
                  :before-upload="beforeBackgroundUpload"
                  accept="image/*,video/mp4,video/webm,video/quicktime"
                >
                  <el-button type="primary" plain icon="el-icon-upload2" :loading="uploadingBackground">
                    {{ backgroundUploadButtonText }}
                  </el-button>
                </el-upload>

                <div v-if="form.backgroundUploadUrl" class="upload-result-card">
                  <div class="upload-result-head">
                    <strong>{{ form.backgroundUploadDisplayName || form.backgroundUploadName }}</strong>
                    <el-tag size="mini" type="success">{{ backgroundUploadTypeLabel }}</el-tag>
                  </div>
                  <img v-if="form.backgroundMediaKind === 'image'" :src="form.backgroundUploadUrl" alt="背景图预览" class="upload-preview-image">
                  <video v-else :src="form.backgroundUploadUrl" class="upload-preview-video" muted controls preload="metadata"></video>
                  <div class="upload-result-actions">
                    <el-button size="mini" icon="el-icon-delete" @click="clearBackgroundUpload">移除背景</el-button>
                  </div>
                </div>

                <p class="field-tip">上传模式支持图片和视频。视频会在后端与数字人透明层合成为最终结果。</p>
              </div>
            </div>
          </div>
        </div>

      </div>

      <div class="batch-panel result-panel">
        <div class="panel-title">
          <h3>任务结果</h3>
          <p>头像预览会随角色与风格实时切换，结果视频生成成功后可直接播放和下载。</p>
        </div>

        <div class="preview-card" :style="previewCardStyle">
          <div class="preview-stage" :style="previewStageStyle">
            <img v-if="previewBackgroundImage" :src="previewBackgroundImage" alt="背景图预览" class="preview-background-image">
            <video v-else-if="previewBackgroundVideo" :src="previewBackgroundVideo" class="preview-background-video" muted autoplay loop playsinline></video>
            <div class="preview-overlay"></div>
            <img :src="currentPreviewImage" :alt="currentAvatar.label" class="preview-avatar-image">
            <div class="preview-badge-row">
              <span>{{ currentAvatar.categoryLabel }}</span>
              <span>{{ currentStyleLabel }}</span>
            </div>
          </div>
          <div class="preview-copy">
            <strong>{{ currentAvatar.chineseLabel }} / {{ currentAvatar.label }}</strong>
            <span>{{ currentVoiceLabel }}</span>
            <span>{{ previewBackgroundLabel }}</span>
          </div>
        </div>

        <div class="style-card">
          <div class="field field-first inline-field">
            <label>人物风格</label>
            <el-select v-model="form.style" class="inline-select">
              <el-option v-for="item in currentStyles" :key="item.value || '__default__'" :label="item.label" :value="item.value" />
            </el-select>
          </div>
        </div>

        <div class="composer-card">
          <div class="field field-first">
            <label>播报文本</label>
            <el-input
              v-model="form.text"
              type="textarea"
              :rows="9"
              maxlength="1200"
              show-word-limit
              placeholder="输入要生成视频的文案。提交后 Azure 会生成完整数字人视频。"
            />
          </div>

          <div class="field">
            <label>快速示例</label>
            <div class="preset-actions-row">
              <button v-for="preset in textPresets" :key="preset.label" type="button" class="preset-btn" @click="form.text = preset.text">
                {{ preset.label }}
              </button>
              <el-button type="primary" icon="el-icon-video-camera-solid" :loading="submitting" @click="submitJob">
                {{ currentJobId ? '重新生成' : '生成视频' }}
              </el-button>
              <el-button icon="el-icon-refresh" :disabled="!currentJobId || polling" @click="refreshJobStatus">查询状态</el-button>
              <el-button icon="el-icon-close" :disabled="!polling" @click="stopPolling">停止轮询</el-button>
            </div>
          </div>
        </div>

        <div class="result-status-card">
          <div class="status-line">
            <span>当前状态</span>
            <el-tag size="small" :type="statusTagType">{{ currentStatus || '未提交' }}</el-tag>
          </div>
          <div class="status-line"><span>任务 ID</span><strong class="mono">{{ currentJobId || '-' }}</strong></div>
          <div class="status-line"><span>区域</span><strong>{{ speechRegion || '-' }}</strong></div>
          <div class="status-line"><span>最近更新时间</span><strong>{{ lastActionDateTime || '-' }}</strong></div>
        </div>

        <el-alert v-if="lastError" :title="lastError" type="error" :closable="false" show-icon class="result-alert" />

        <div v-if="resultVideoUrl" class="video-result-card">
          <video class="result-video" :src="resultVideoUrl" controls preload="metadata"></video>
          <div class="result-actions">
            <el-button type="primary" icon="el-icon-download" @click="downloadResult">下载视频</el-button>
            <el-button icon="el-icon-link" @click="openResultInNewTab">新窗口打开</el-button>
            <el-button v-if="summaryUrl" icon="el-icon-document" @click="openSummary">查看摘要</el-button>
          </div>
        </div>

        <div class="log-card">
          <div class="log-head">
            <h4>任务日志</h4>
            <el-button size="mini" @click="logs = []">清空</el-button>
          </div>
          <div class="log-list">
            <div v-for="item in logs" :key="item.id" class="log-item" :data-type="item.type">
              <span class="log-time">{{ item.time }}</span>
              <span class="log-message">{{ item.message }}</span>
            </div>
            <p v-if="!logs.length" class="log-empty">还没有任务日志。</p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { API_BASE_URL } from '@/config.js'

const API_ROOT = String(API_BASE_URL || '').replace(/\/$/, '')
const DOC_MEDIA_ROOT = 'https://learn.microsoft.com/en-us/azure/ai-services/speech-service/text-to-speech-avatar/media/'

const STYLE_LABELS = {
  'casual-sitting': '休闲坐姿',
  'graceful-sitting': '优雅坐姿',
  'graceful-standing': '优雅站姿',
  'technical-sitting': '技术讲解坐姿',
  'technical-standing': '技术讲解站姿',
  casual: '休闲形象',
  formal: '正式形象',
  graceful: '优雅形象',
  business: '商务形象',
  youthful: '青春形象',
  default: '标准照片头像'
}

function mediaUrl(fileName) {
  return DOC_MEDIA_ROOT + fileName
}

function videoAvatar(value, label, chineseLabel, accent, styles) {
  return {
    value,
    label,
    chineseLabel,
    accent,
    category: 'video',
    categoryLabel: '标准视频头像',
    styles: styles.map(([style]) => ({
      value: style,
      label: STYLE_LABELS[style] || style,
      image: mediaUrl(`${value}-${style}.png`)
    }))
  }
}

const AVATAR_CHARACTERS = [
  videoAvatar('harry', 'Harry', '哈里', '#ffb34d', [['business'], ['casual'], ['youthful']]),
  videoAvatar('jeff', 'Jeff', '杰夫', '#3fb47f', [['business'], ['formal']]),
  videoAvatar('lisa', 'Lisa', '丽莎', '#699dff', [['casual-sitting'], ['graceful-sitting'], ['graceful-standing'], ['technical-sitting'], ['technical-standing']]),
  videoAvatar('lori', 'Lori', '洛丽', '#ff88a9', [['casual'], ['graceful'], ['formal']]),
  videoAvatar('max', 'Max', '马克斯', '#4cb6a7', [['business'], ['casual'], ['formal']]),
  videoAvatar('meg', 'Meg', '梅格', '#8e7cff', [['business'], ['casual'], ['formal']])
]

const VOICE_OPTIONS = [
  { value: 'zh-CN-XiaoxiaoNeural', label: '晓晓（中文，女声）' },
  { value: 'zh-CN-YunxiNeural', label: '云希（中文，男声）' },
  { value: 'en-US-AvaMultilingualNeural', label: 'Ava Multilingual（多语言，女声）' },
  { value: 'en-US-AndrewMultilingualNeural', label: 'Andrew Multilingual（多语言，男声）' }
]

const TEXT_PRESETS = [
  { label: '欢迎词', text: '你好，欢迎来到数字人视频生成工具。这段文本会被合成为完整的视频。' },
  { label: '产品介绍', text: '本工具会调用微软 Azure Batch Avatar 接口，生成一个可以直接下载的数字人视频文件。' },
  { label: '中文播报', text: '现在进行中文数字人视频生成测试，请在结果完成后点击下载视频。' }
]

function formatNow() {
  const d = new Date()
  const pad = (value) => String(value).padStart(2, '0')
  return `${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`
}

function normalizeUrl(url) {
  const value = String(url || '').trim()
  if (!value) return ''
  if (/^https?:\/\//i.test(value)) return value
  if (value.startsWith('/')) return API_ROOT + value
  return value
}

export default {
  name: 'AvatarVideoGenerator',
  data() {
    return {
      avatarCharacters: AVATAR_CHARACTERS,
      voiceOptions: VOICE_OPTIONS,
      textPresets: TEXT_PRESETS,
      form: {
        character: 'lisa',
        style: 'casual-sitting',
        voice: 'zh-CN-XiaoxiaoNeural',
        videoFormat: 'mp4',
        backgroundMode: 'color',
        backgroundColor: '#F3F7FF',
        backgroundImage: '',
        backgroundUploadName: '',
        backgroundUploadDisplayName: '',
        backgroundUploadUrl: '',
        backgroundMediaKind: '',
        text: TEXT_PRESETS[0].text
      },
      runtimeAvailable: false,
      speechRegion: '',
      activeSection: 'avatar',
      submitting: false,
      uploadingBackground: false,
      polling: false,
      pollTimer: null,
      currentJobId: '',
      currentStatus: '',
      lastActionDateTime: '',
      resultVideoUrl: '',
      summaryUrl: '',
      lastError: '',
      logs: [],
      logSeq: 0
    }
  },
  computed: {
    avatarCharacterGroups() {
      return [
        { label: '标准视频头像', items: this.avatarCharacters.filter(item => item.category === 'video') }
      ]
    },
    currentAvatar() {
      return this.avatarCharacters.find(item => item.value === this.form.character) || this.avatarCharacters[0]
    },
    currentStyles() {
      return this.currentAvatar.styles || []
    },
    currentStyleOption() {
      return this.currentStyles.find(item => item.value === this.form.style) || this.currentStyles[0] || { value: '', label: '默认', image: '' }
    },
    currentStyleLabel() {
      return this.currentStyleOption.label || STYLE_LABELS[this.form.style] || '默认'
    },
    currentPreviewImage() {
      return this.currentStyleOption.image || ''
    },
    currentVoiceLabel() {
      const match = this.voiceOptions.find(item => item.value === this.form.voice)
      return match ? match.label : this.form.voice
    },
    previewCardStyle() {
      const accent = this.currentAvatar.accent || '#6aa8ff'
      return { background: `radial-gradient(circle at top left, ${accent}44, transparent 34%), linear-gradient(180deg, rgba(255,255,255,0.96), rgba(241,245,249,0.96))` }
    },
    previewStageStyle() {
      if (this.form.backgroundMode === 'color') {
        const color = this.normalizeBackgroundColor(this.form.backgroundColor)
        return {
          background: `linear-gradient(180deg, ${color}, ${color}), linear-gradient(135deg, rgba(99,102,241,.1), rgba(56,189,248,.12))`
        }
      }
      return {}
    },
    previewBackgroundImage() {
      if (this.form.backgroundMode === 'url') return normalizeUrl(this.form.backgroundImage)
      if (this.form.backgroundMode === 'upload' && this.form.backgroundMediaKind === 'image') return normalizeUrl(this.form.backgroundUploadUrl)
      return ''
    },
    previewBackgroundVideo() {
      if (this.form.backgroundMode === 'upload' && this.form.backgroundMediaKind === 'video') return normalizeUrl(this.form.backgroundUploadUrl)
      return ''
    },
    previewBackgroundLabel() {
      if (this.form.backgroundMode === 'color') return `背景：纯色 ${this.normalizeBackgroundColor(this.form.backgroundColor)}`
      if (this.form.backgroundMode === 'url') return this.form.backgroundImage ? '背景：公网图片 URL' : '背景：未填写 URL'
      if (this.form.backgroundUploadUrl) return `背景：已上传${this.backgroundUploadTypeLabel}`
      return '背景：未上传文件'
    },
    backgroundUploadTypeLabel() {
      return this.form.backgroundMediaKind === 'video' ? '背景视频' : '背景图片'
    },
    backgroundUploadButtonText() {
      if (this.uploadingBackground) return '上传中...'
      return this.form.backgroundUploadUrl ? '重新上传背景' : '选择背景文件'
    },
    statusTagType() {
      if (this.currentStatus === 'Succeeded') return 'success'
      if (this.currentStatus === 'Failed') return 'danger'
      if (this.currentStatus === 'Running' || this.currentStatus === 'NotStarted') return 'warning'
      return 'info'
    }
  },
  watch: {
    'form.character'(value) {
      const match = this.avatarCharacters.find(item => item.value === value)
      if (!match) return
      if (!match.styles.some(item => item.value === this.form.style)) {
        this.form.style = match.styles[0] ? match.styles[0].value : ''
      }
    },
    'form.backgroundMode'(value) {
      if (value === 'color') this.form.backgroundImage = ''
    }
  },
  mounted() {
    this.loadRuntime()
  },
  beforeDestroy() {
    this.stopPolling()
  },
  methods: {
    selectAvatar(item) {
      if (!item || !item.value) return
      this.form.character = item.value
    },
    getAvatarCardImage(item) {
      return item && item.styles && item.styles[0] ? item.styles[0].image : ''
    },
    log(message, type = 'info') {
      this.logs.unshift({ id: ++this.logSeq, time: formatNow(), type, message })
      this.logs = this.logs.slice(0, 50)
    },
    normalizeBackgroundColor(value) {
      const color = String(value || '').trim().replace('#', '')
      if (/^[0-9a-fA-F]{8}$/.test(color)) return `#${color.toUpperCase()}`
      if (/^[0-9a-fA-F]{6}$/.test(color)) return `#${color.toUpperCase()}FF`
      return '#F3F7FFFF'
    },
    beforeBackgroundUpload(file) {
      const isImage = /^image\//.test(file.type)
      const isVideo = /^video\/(mp4|webm|quicktime)$/.test(file.type)
      if (!isImage && !isVideo) {
        this.$message.error('仅支持上传图片或 MP4 / WebM / MOV 视频')
        return false
      }
      if (file.size > 200 * 1024 * 1024) {
        this.$message.error('背景文件不能超过 200MB')
        return false
      }
      return true
    },
    async uploadBackgroundFile(option) {
      const file = option && option.file
      if (!file) return
      const formData = new FormData()
      formData.append('background', file)
      this.uploadingBackground = true
      this.lastError = ''
      try {
        const response = await this.$axios.post('/avatar-batch/background-upload', formData)
        const data = response.data || {}
        this.form.backgroundMode = 'upload'
        this.form.backgroundUploadName = data.filename || ''
        this.form.backgroundUploadDisplayName = data.originalName || file.name || ''
        this.form.backgroundUploadUrl = data.url || ''
        this.form.backgroundMediaKind = data.mediaKind || ''
        this.log(`背景文件上传成功：${this.form.backgroundUploadDisplayName}`)
        if (option.onSuccess) option.onSuccess(data, file)
      } catch (error) {
        const message = this.extractError(error, '背景文件上传失败')
        this.lastError = message
        this.log(message, 'error')
        if (option.onError) option.onError(error)
      } finally {
        this.uploadingBackground = false
      }
    },
    clearBackgroundUpload() {
      this.form.backgroundUploadName = ''
      this.form.backgroundUploadDisplayName = ''
      this.form.backgroundUploadUrl = ''
      this.form.backgroundMediaKind = ''
      this.log('已移除上传背景')
    },
    async loadRuntime() {
      try {
        const response = await this.$axios.get('/avatar-batch/runtime-config')
        const data = response.data || {}
        this.runtimeAvailable = !!data.available
        this.speechRegion = data.speechRegion || ''
        if (data.available) this.log(`已加载批量 Avatar 服务配置，区域 ${this.speechRegion}`)
        else {
          this.lastError = data.message || '数字人视频服务未配置'
          this.log(this.lastError, 'error')
        }
      } catch (error) {
        this.lastError = this.extractError(error, '读取批量 Avatar 配置失败')
        this.log(this.lastError, 'error')
      }
    },
    async submitJob() {
      const text = String(this.form.text || '').trim()
      if (!text) return this.$message.warning('请先输入要生成视频的文本')
      if (this.form.backgroundMode === 'url' && !String(this.form.backgroundImage || '').trim()) {
        return this.$message.warning('URL 模式下请填写背景图地址')
      }
      if (this.form.backgroundMode === 'upload' && !this.form.backgroundUploadName) {
        return this.$message.warning('上传模式下请先上传背景图片或视频')
      }
      this.stopPolling()
      this.submitting = true
      this.resultVideoUrl = ''
      this.summaryUrl = ''
      this.lastError = ''
      try {
        const response = await this.$axios.post('/avatar-batch/jobs', {
          text,
          voice: this.form.voice,
          character: this.form.character,
          style: this.form.style,
          videoFormat: this.form.videoFormat,
          backgroundMode: this.form.backgroundMode,
          backgroundColor: this.form.backgroundColor,
          backgroundImage: this.form.backgroundImage,
          backgroundUploadFilename: this.form.backgroundUploadName,
          backgroundMediaKind: this.form.backgroundMediaKind
        })
        const data = response.data || {}
        this.currentJobId = data.id || ''
        this.currentStatus = data.status || 'NotStarted'
        this.lastActionDateTime = data.lastActionDateTime || ''
        this.speechRegion = data.speechRegion || this.speechRegion
        this.log(`任务已提交，ID ${this.currentJobId}`)
        if (data.renderMode === 'local-composite') this.log('已启用本地背景合成，系统会先生成透明数字人层再输出最终视频')
        this.startPolling()
      } catch (error) {
        this.lastError = this.extractError(error, '提交批量合成任务失败')
        this.log(this.lastError, 'error')
      } finally {
        this.submitting = false
      }
    },
    startPolling() {
      if (!this.currentJobId) return
      this.stopPolling()
      this.polling = true
      this.refreshJobStatus()
      this.pollTimer = setInterval(() => this.refreshJobStatus(), 10000)
    },
    stopPolling() {
      if (this.pollTimer) clearInterval(this.pollTimer)
      this.pollTimer = null
      this.polling = false
    },
    async refreshJobStatus() {
      if (!this.currentJobId) return
      try {
        const response = await this.$axios.get(`/avatar-batch/jobs/${encodeURIComponent(this.currentJobId)}`)
        const data = response.data || {}
        this.currentStatus = data.status || this.currentStatus
        this.lastActionDateTime = data.lastActionDateTime || this.lastActionDateTime
        this.resultVideoUrl = normalizeUrl(data.resultUrl || '')
        this.summaryUrl = normalizeUrl(data.summaryUrl || '')
        this.lastError = data.errorMessage || ''
        this.log(`任务状态更新为 ${this.currentStatus}`)
        if (this.currentStatus === 'Succeeded' || this.currentStatus === 'Failed') this.stopPolling()
      } catch (error) {
        this.lastError = this.extractError(error, '查询任务状态失败')
        this.log(this.lastError, 'error')
        this.stopPolling()
      }
    },
    downloadResult() {
      if (!this.resultVideoUrl) return
      const a = document.createElement('a')
      a.href = this.resultVideoUrl
      a.target = '_blank'
      a.rel = 'noopener noreferrer'
      a.download = ''
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
    },
    openResultInNewTab() {
      if (!this.resultVideoUrl) return
      window.open(this.resultVideoUrl, '_blank', 'noopener,noreferrer')
    },
    openSummary() {
      if (!this.summaryUrl) return
      window.open(this.summaryUrl, '_blank', 'noopener,noreferrer')
    },
    extractError(error, fallback) {
      if (error && error.response && error.response.data) {
        const data = error.response.data
        if (typeof data === 'string') return data
        if (data.message) return data.message
      }
      if (error && error.message) return error.message
      return fallback
    }
  }
}
</script>

<style lang="less" scoped>
.avatar-batch-page { display: flex; flex-direction: column; gap: 20px; color: var(--text-primary); }
.batch-header, .batch-panel { border: 1px solid var(--border-color); border-radius: 22px; background: radial-gradient(circle at top left, rgba(64,158,255,.14), transparent 42%), linear-gradient(180deg, rgba(255,255,255,.97), rgba(245,248,255,.97)); box-shadow: 0 12px 32px rgba(15,23,42,.08); }
.batch-header { display: flex; align-items: center; justify-content: space-between; gap: 16px; padding: 28px 30px; }
.batch-kicker { margin: 0 0 8px; color: #2c6cf6; font-weight: 700; letter-spacing: .08em; text-transform: uppercase; }
.batch-header h2 { margin: 0; font-size: 34px; }
.batch-desc { margin: 12px 0 0; color: var(--text-regular); line-height: 1.8; }
.batch-status-chip { flex-shrink: 0; min-height: 34px; padding: 0 14px; border-radius: 999px; display: inline-flex; align-items: center; background: rgba(245,108,108,.12); color: #f56c6c; font-weight: 700; }
.batch-status-chip.ok { background: rgba(103,194,58,.12); color: #67c23a; }
.batch-layout { display: grid; grid-template-columns: minmax(360px,460px) minmax(0,1fr); gap: 20px; }
.batch-panel { padding: 24px; }
.panel-title h3, .log-head h4 { margin: 0; }
.panel-title p { margin: 8px 0 0; color: var(--text-secondary); }
.batch-alert, .field, .preview-card, .result-status-card, .video-result-card, .log-card { margin-top: 18px; }
.field-grid { display: grid; grid-template-columns: repeat(2, minmax(0,1fr)); gap: 14px; margin-top: 18px; }
.field label { display: block; margin-bottom: 8px; font-size: 13px; font-weight: 700; color: var(--text-secondary); }
.field-tip, .preview-copy span, .panel-title p, .batch-desc, .log-empty { line-height: 1.7; color: var(--text-secondary); }
.inline-field { display: grid; grid-template-columns: 72px minmax(0, 240px); align-items: center; gap: 12px; }
.inline-field label { margin-bottom: 0; }
.inline-select { width: 100%; }
.config-shell { display: grid; grid-template-columns: 88px minmax(0, 1fr); gap: 18px; margin-top: 18px; align-items: start; }
.config-nav { display: flex; flex-direction: column; gap: 10px; position: sticky; top: 12px; }
.config-nav-item { min-height: 72px; padding: 12px 10px; border-radius: 18px; border: 1px solid rgba(148,163,184,.22); background: rgba(255,255,255,.8); color: var(--text-secondary); font-size: 14px; font-weight: 700; cursor: pointer; transition: border-color .18s ease, color .18s ease, background .18s ease, transform .18s ease; }
.config-nav-item:hover { transform: translateY(-1px); border-color: rgba(44,108,246,.28); color: #2c6cf6; }
.config-nav-item.active { border-color: #2c6cf6; background: linear-gradient(180deg, rgba(44,108,246,.12), rgba(44,108,246,.04)); color: #2c6cf6; box-shadow: 0 0 0 3px rgba(44,108,246,.1); }
.config-content { min-width: 0; }
.config-pane { min-width: 0; }
.field-first { margin-top: 0; }
.avatar-gallery-container { max-height: 760px; padding: 2px 4px 2px 2px; overflow: auto; border-radius: 18px; }
.avatar-gallery { display: flex; flex-direction: column; gap: 18px; }
.avatar-group-title { margin-bottom: 10px; font-size: 13px; font-weight: 700; color: var(--text-primary); }
.avatar-card-grid { display: grid; grid-template-columns: repeat(2, minmax(0, 113px)); gap: 14px; justify-content: start; }
.avatar-card { box-sizing: border-box; width: 100%; padding: 10px 10px 12px; overflow: hidden; border: 1px solid rgba(148,163,184,.24); border-radius: 18px; background: rgba(255,255,255,.85); cursor: pointer; text-align: center; transition: transform .18s ease, border-color .18s ease, box-shadow .18s ease; }
.avatar-card:hover { transform: translateY(-2px); border-color: rgba(44,108,246,.36); box-shadow: 0 10px 20px rgba(15,23,42,.08); }
.avatar-card.active { border-color: #2c6cf6; box-shadow: 0 0 0 3px rgba(44,108,246,.12); background: rgba(44,108,246,.04); }
.avatar-card-image-box { width: 100%; max-width: 113px; height: 146px; margin: 0 auto 10px; padding: 6px; box-sizing: border-box; border-radius: 14px; overflow: hidden; background: linear-gradient(180deg, rgba(241,245,249,.95), rgba(226,232,240,.9)); display: flex; align-items: flex-end; justify-content: center; }
.avatar-card-image-box.photo { max-width: 83px; height: 80px; align-items: center; }
.avatar-card-image { display: block; max-width: 100%; max-height: 100%; width: auto; height: auto; object-fit: contain; object-position: center bottom; }
.avatar-card-name { font-size: 13px; font-weight: 700; color: var(--text-primary); line-height: 1.45; word-break: break-word; }
.color-row { display: grid; grid-template-columns: 48px minmax(0,1fr); gap: 10px; }
.background-uploader { display: inline-flex; }
.upload-result-card { margin-top: 12px; padding: 14px; border-radius: 16px; border: 1px solid rgba(44,108,246,.14); background: rgba(44,108,246,.04); }
.upload-result-head, .log-head, .status-line { display: flex; align-items: center; justify-content: space-between; gap: 12px; }
.upload-preview-image, .upload-preview-video { display: block; width: 100%; margin-top: 12px; border-radius: 14px; background: #111827; }
.upload-preview-image { max-height: 220px; object-fit: cover; }
.upload-preview-video { max-height: 220px; }
.upload-result-actions, .preset-list, .form-actions, .result-actions { display: flex; flex-wrap: wrap; gap: 10px; }
.preset-actions-row { display: flex; flex-wrap: wrap; align-items: center; gap: 10px; }
.upload-result-actions { justify-content: flex-end; margin-top: 12px; }
.preset-btn { border: 1px solid rgba(44,108,246,.18); background: rgba(44,108,246,.08); color: #2c6cf6; border-radius: 999px; padding: 8px 14px; cursor: pointer; }
.preview-card, .style-card, .result-status-card, .video-result-card, .log-card { padding: 18px; border-radius: 18px; border: 1px solid var(--border-color); background: var(--bg-secondary); }
.composer-card { margin-top: 18px; padding: 18px; border-radius: 18px; border: 1px solid var(--border-color); background: var(--bg-secondary); }
.preview-card { display: grid; grid-template-columns: minmax(0,320px) minmax(0,1fr); gap: 18px; align-items: center; }
.preview-stage { position: relative; min-height: 360px; overflow: hidden; border-radius: 18px; border: 1px solid rgba(148,163,184,.18); background: linear-gradient(180deg, rgba(255,255,255,.82), rgba(241,245,249,.9)), linear-gradient(135deg, rgba(99,102,241,.1), rgba(56,189,248,.12)); display: flex; align-items: flex-end; justify-content: center; }
.preview-background-image, .preview-background-video, .preview-overlay { position: absolute; inset: 0; }
.preview-background-image, .preview-background-video { width: 100%; height: 100%; object-fit: cover; }
.preview-overlay { background: linear-gradient(180deg, rgba(15,23,42,.02), rgba(15,23,42,.18)), radial-gradient(circle at top, rgba(255,255,255,.46), transparent 42%); }
.preview-avatar-image { position: relative; z-index: 1; width: min(82%, 280px); max-height: 330px; object-fit: contain; filter: drop-shadow(0 16px 28px rgba(15,23,42,.18)); }
.preview-badge-row { position: absolute; left: 14px; bottom: 14px; z-index: 2; display: flex; flex-wrap: wrap; gap: 8px; }
.preview-badge-row span { padding: 6px 10px; border-radius: 999px; background: rgba(15,23,42,.72); color: #fff; font-size: 12px; }
.preview-copy { display: flex; flex-direction: column; gap: 8px; }
.status-line + .status-line { margin-top: 12px; }
.mono, .log-time { font-family: Consolas, 'Courier New', monospace; }
.mono { font-size: 12px; }
.result-video { display: block; width: 100%; border-radius: 14px; background: #000; }
.log-list { margin-top: 14px; display: flex; flex-direction: column; gap: 10px; max-height: 260px; overflow: auto; }
.log-item { display: grid; grid-template-columns: 82px minmax(0,1fr); gap: 10px; padding: 10px 12px; border-radius: 12px; background: var(--bg-primary); border: 1px solid var(--border-light); }
.log-item[data-type='error'] { border-color: rgba(245,108,108,.28); background: rgba(245,108,108,.06); }
.log-message, .alert-copy { line-height: 1.7; }
@media (max-width: 992px) { .preview-card { grid-template-columns: 1fr; } }
@media (max-width: 768px) {
  .batch-layout, .field-grid, .config-shell { grid-template-columns: 1fr; }
  .batch-header { flex-direction: column; align-items: flex-start; }
  .batch-header h2 { font-size: 28px; }
  .preview-stage { min-height: 300px; }
  .inline-field { grid-template-columns: 1fr; }
  .inline-field label { margin-bottom: 8px; }
  .config-nav { position: static; flex-direction: row; }
  .config-nav-item { flex: 1; min-height: 52px; }
  .avatar-gallery-container { max-height: none; padding-right: 0; overflow: visible; }
  .avatar-card-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); }
  .avatar-card-image-box { width: 100%; max-width: 113px; height: 146px; }
  .avatar-card-image-box.photo { max-width: 83px; height: 80px; }
}
</style>

<style lang="less">
#app.dark-mode {
  .batch-header, .batch-panel { background: radial-gradient(circle at top left, rgba(64,158,255,.16), transparent 42%), linear-gradient(180deg, rgba(24,33,54,.96), rgba(21,28,46,.96)); }
  .preview-card, .style-card, .composer-card, .result-status-card, .video-result-card, .log-card { background: rgba(15,23,42,.42); }
  .preview-stage { background: linear-gradient(180deg, rgba(30,41,59,.9), rgba(15,23,42,.92)), linear-gradient(135deg, rgba(99,102,241,.14), rgba(56,189,248,.16)); }
  .preset-btn { border-color: rgba(96,165,250,.24); background: rgba(96,165,250,.1); color: #8ab4ff; }
  .upload-result-card { border-color: rgba(96,165,250,.24); background: rgba(96,165,250,.1); }
  .log-item { background: rgba(15,23,42,.54); }
  .config-nav-item { background: rgba(15,23,42,.5); border-color: rgba(148,163,184,.18); }
  .config-nav-item.active { background: rgba(44,108,246,.22); color: #8ab4ff; }
  .avatar-card { background: rgba(15,23,42,.5); border-color: rgba(148,163,184,.18); }
  .avatar-card.active { background: rgba(44,108,246,.18); }
  .avatar-card-image-box { background: linear-gradient(180deg, rgba(30,41,59,.95), rgba(15,23,42,.92)); }
}
</style>
